import { componentText, SchmancyElement } from '@schmancy/mixins/index'
import { css, html, PropertyValues } from 'lit'
import { customElement, property, queryAssignedElements, state } from 'lit/decorators.js'
import { choose } from 'lit/directives/choose.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { BehaviorSubject, EMPTY, fromEvent, merge, Observable, takeUntil } from 'rxjs'
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators'
import type { NavRailItemDescriptor, NavRailNode, NestedRails } from './context'
import { SchmancyNavigationRailItem } from './navigation-rail-item'
// The column's scroll region, alongside the puck and the sheet it also
// renders. Consumers that auto-import per template (the schmancyAutoImport
// vite plugin) scan their OWN templates, never schmancy's, so a tag this
// component renders and does not import stays undefined in any app whose
// entry does not name it — leaving the column an inert unknown element that
// clips nothing and scrolls nowhere.
import '../layout/scroll'
import './navigation-rail-puck'
import './navigation-rail-sheet'
import {
	navRailAbsorbed,
	navRailActiveChild,
	navRailDepth,
	navRailIsVisible,
	navRailSelectionMoved,
	navRailTreeChanged$,
	navRailVisibilityMoved,
	navRailVisibleAncestor,
	registerRail,
	unregisterRail,
} from './registry'

export type NavigateEvent = CustomEvent<string>

export type NavigationRailMenuClickEvent = CustomEvent<void>

export type NavigationRailFabClickEvent = CustomEvent<void>

/**
 * 'all' shows every item's label under its icon, 'selected' shows only the
 * active item's, 'none' hides all. The rail's own column only — the cascade's
 * rows and the puck's drill always carry their labels.
 */
export type LabelVisibility = 'all' | 'selected' | 'none'

/**
 * The three tab treatments `direction="row"` carries. `segmented` is the
 * shipped default — a `primary-container` pill echoing the column's own
 * expanded-mode indicator, at a controlled, secondary-strength accent dose.
 * `scale` and `elevated` carry zero accent — hierarchy from type-scale/weight
 * or from elevation/fill instead — for a page where even that controlled
 * dose is one accent meaning too many.
 */
export type NavigationRailVariant = 'segmented' | 'scale' | 'elevated'

/**
 * Where the CASCADE's labelled column stops being chrome and starts being a
 * claimant on the work beside it — below this a cascading rail draws itself at
 * the same 80dp measure the default column always uses.
 *
 * A quarter of the frame is where chrome stops framing the work and starts
 * competing with it, and at `lg` the 208px column spends 20.3% — inside that
 * quarter with room to spare. The quarter on its own would now put this
 * threshold at 832 (208 × 4), a hair above `md`, where the puck has already
 * taken navigation over: the compact band between the two would be 64px wide
 * and a tablet frame would carry a labelled column nearly to the puck's step.
 * `lg` is held so that band still exists — under it the frame beside the rail
 * is a tablet's, and 80dp of navigation leaves it its work. Below `md` the column has already
 * left for the puck, so compact is exactly the band between.
 *
 * The VIEWPORT, like the `md` step this rail already reads, and not a
 * container query: this rail is the frame's first column and sizes itself, so
 * it has no allocated box to query — asking one would be asking itself. Read
 * fresh in every `render()`, so nothing here can go stale or lose a mount
 * order.
 */
const compactColumn = typeof window === 'undefined' ? undefined : window.matchMedia('(max-width: 1023.98px)')

const compactColumnChanged$ = compactColumn ? fromEvent(compactColumn, 'change') : EMPTY

/**
 * A vertical navigation component positioned on the left side of an application,
 * providing access to 3-7 primary destinations with a compact footprint.
 *
 * By default a rail draws its OWN 80dp column, carrying its own slotted items
 * and nothing else, wherever its consumer mounted it — so a section rail
 * routed inside a shell opens a second column beside the shell's, which is
 * where its consumer put it and where the operator looks for it. `nestedRails`
 * is the one seam that changes this: set to `cascade`, a rail draws every
 * level registered beneath it inside its own column as one indented tree
 * (`navigation-rail-sheet.ts`, the same renderer the mobile puck's drill
 * mounts) and those rails draw nothing of their own.
 *
 * Below `md` the column steps aside for the puck, which is navigation, not
 * the absence of it, and the puck's drill draws the whole tree in both modes —
 * so `nestedRails` is a desktop decision only. Exactly one puck is on screen:
 * whoever has no visible rail above it carries it.
 *
 * A consumer that hides a rail from OUTSIDE — a route that renders its shell
 * without one — takes only that rail off screen: both the column and the puck
 * move down to the topmost rail anyone can still see, so a surface whose
 * ancestor rail is hidden draws its own rows rather than losing navigation
 * entirely. (`theme.setFullscreen` remains a theme primitive with its own
 * consumers — the mobile `<schmancy-navigation-bar>`, the drawer, the `fill()`
 * and layout directives' bottom offset; this rail simply does not listen.)
 *
 * `direction="row"` is a second, distinct personality living in the same
 * element: M3 treats a rail and a tab bar as different components for a
 * reason (a rail is an icon-led persistent column that collapses below `md`;
 * a row is a lightweight inline strip with no icon requirement and no
 * device-collapse story — it already reads fine at any width), so `row`
 * bypasses the column render entirely and draws a `role="tablist"` of plain
 * buttons straight off `items()`, in one of three `variant` treatments. The
 * item AUTHORING api stays identical either way — a consumer slots real
 * `<schmancy-navigation-rail-item value label icon>` children regardless of
 * `direction`; in `row` they are the hidden data source only, read via the
 * same value/label/active properties `items()` already projects, and their
 * `icon` is not drawn (a row's label carries the whole width it needs — no
 * truncation, `overflow-x-auto` instead). `nestedRails` and `labelVisibility`
 * are column-only: a row is one strip of tabs with no column to cascade a
 * nested level inside, and always carries every label. `expanded` (below) is
 * likewise column-only — a row's tabs already lay icon-less label text out
 * side by side.
 * @see https://m3.material.io/components/navigation-rail/overview
 *
 * @element schmancy-navigation-rail
 * @slot fab - Slot for a floating action button at the top (column only)
 * @slot menu - Slot for a menu icon or button below the FAB (column only)
 * @slot header - Custom header content slot (column only)
 * @slot footer - Custom footer content slot (column only). Below `md` the
 *   puck's sheet draws these same elements beneath its rows, and activating
 *   one there closes the sheet (`navigation-rail-sheet.ts`'s `footer`)
 * @slot sheet-header - The phone sheet's header row, never drawn by the
 *   column. Below `md` the puck's sheet draws these elements as one row above
 *   its rows — the first leading, the last trailing (a mark, then a plate) —
 *   so the column's own mark and plate stay where they are on desktop
 *   (`navigation-rail-sheet.ts`'s `header`)
 * @slot - Default slot for navigation rail items — the rows of the rail's own
 *   column (or, in `row` direction, the tab strip's data source), and the
 *   item source the cascade and the puck's drill both project from (hidden
 *   while either of those draws them instead)
 *
 * @fires navigate - When a navigation item is selected
 * @fires menu-click - When the menu button is clicked (column only)
 * @fires fab-click - When the FAB is clicked (column only)
 *
 * @csspart rail - The main rail container (column only)
 * @csspart header - The header section (column only)
 * @csspart nav - The navigation scroll region (column only)
 * @csspart items - The layout container for the rail's own rows (column only)
 * @csspart footer - The footer section (column only)
 * @csspart tablist - The row of tab buttons (row only)
 *
 * @attr absorbed - Written by the rail itself, never by a consumer: the
 *   registry's own verdict that a cascading column above is already drawing
 *   these rows, reflected so the host can drop its box while it is true.
 *
 * @example
 * <schmancy-navigation-rail activeIndex="0" label-visibility="selected">
 *   <schmancy-button slot="fab" variant="filled" aria-label="Compose">
 *     <schmancy-icon>add</schmancy-icon>
 *   </schmancy-button>
 *   <schmancy-button slot="menu" variant="text" aria-label="Menu">
 *     <schmancy-icon>menu</schmancy-icon>
 *   </schmancy-button>
 *   <schmancy-navigation-rail-item icon="home" label="Home"></schmancy-navigation-rail-item>
 *   <schmancy-navigation-rail-item icon="search" label="Search"></schmancy-navigation-rail-item>
 *   <schmancy-navigation-rail-item icon="favorite" label="Favorites" badge="3"></schmancy-navigation-rail-item>
 *   <schmancy-navigation-rail-item icon="settings" label="Settings"></schmancy-navigation-rail-item>
 * </schmancy-navigation-rail>
 *
 * @example
 * <schmancy-navigation-rail direction="row" label="Legal">
 *   <schmancy-navigation-rail-item value="terms" label="Terms of Service"></schmancy-navigation-rail-item>
 *   <schmancy-navigation-rail-item value="privacy" label="Privacy Policy"></schmancy-navigation-rail-item>
 * </schmancy-navigation-rail>
 */
@customElement('schmancy-navigation-rail')
export class SchmancyNavigationRail extends SchmancyElement implements NavRailNode {
	static styles = [
		css`
			:host {
				display: block;
				min-width: 0;
				min-height: 0;
			}

			/*
			 * Absorbed, everything this element renders is one hidden slot — and a
			 * host that still generates a box for that holds a place in its
			 * consumer's own flex or grid: a row, a gap, a track. Measured on the
			 * settings legal surface at 1280×720, a 0-height absorbed strip still
			 * spent the cluster's 12px gap and pushed every sibling below it down
			 * by that much.
			 *
			 * Conditional and never blanket: an unabsorbed host IS the box the
			 * consumer laid out — the column reserves its own width there and a
			 * strip carries the consumer's own classes on it — so only the verdict
			 * that leaves nothing to draw takes the box away.
			 */
			:host([absorbed]) {
				display: contents;
			}
		`,
	]

	private activeIndex$ = new BehaviorSubject<number>(-1)

	@property({ type: Number })
	get activeIndex() {
		return this.activeIndex$.value
	}
	set activeIndex(value: number) {
		this.activeIndex$.next(value)
	}

	/** Alternative to `activeIndex` — select by value instead of index. */
	@property({ type: String })
	get activeValue() {
		return this.#activeValue
	}
	set activeValue(value: string) {
		this.#activeValue = value
		this.updateActiveByValue(value)
	}
	#activeValue = ''

	/**
	 * What this rail does with the rails registered beneath it — its own
	 * column each (the default), or one indented cascade inside this one. See
	 * `NestedRails` in `context.ts` for the full contract; it is read off the
	 * ANCESTOR, so setting it here decides what happens to everything below,
	 * never to this rail itself. Column direction only — a row is one strip
	 * with no column to cascade a nested level inside, so it never absorbs
	 * the rails beneath it regardless of this property.
	 */
	@property({ type: String, attribute: 'nested-rails', reflect: true })
	nestedRails: NestedRails = 'in-place'

	/**
	 * Which of this rail's own rows carry their label. Ignored while the rows
	 * on screen are not this rail's own — a cascade's rows and the puck's
	 * drill rows are laid out beside their icons with room for both. Column
	 * direction only — a row always carries every label (see `direction`).
	 */
	@property({ type: String, attribute: 'label-visibility', reflect: true })
	labelVisibility: LabelVisibility = 'all'

	@property({ type: String, reflect: true })
	alignment: 'top' | 'center' | 'bottom' = 'top'

	/**
	 * M3's own collapsed/expanded distinction
	 * (https://m3.material.io/components/navigation-rail/overview): a rail is
	 * a vertical list of destinations in BOTH states — expanded never turns it
	 * into a horizontal strip (see `direction` for that, a distinct axis), it
	 * is the wider column that stands in for a navigation drawer. `false`
	 * (default) is the compact 80dp column, icon stacked over label. `true`
	 * widens to the same 208px measure and row anatomy the cascade's own
	 * labelled column already draws (`SchmancyNavigationRailItem.expanded`,
	 * icon beside label) — reused rather than a second measure invented for
	 * this, so an expanded rail and a cascading one read as the same system.
	 * Column direction only.
	 *
	 * Every other feature — the header/footer/fab slots, `nestedRails`,
	 * `labelVisibility`, tooltips — is unchanged by this.
	 */
	@property({ type: Boolean, reflect: true })
	expanded = false

	/**
	 * `column` (default) is the vertical rail described above. `row` is the
	 * tab-strip personality — see the class doc's `direction="row"` paragraph
	 * for the full contract.
	 */
	@property({ type: String, reflect: true })
	direction: 'column' | 'row' = 'column'

	/** Which of the three tab treatments a `row` direction renders. Ignored in `column` direction. */
	@property({ type: String, reflect: true })
	variant: NavigationRailVariant = 'segmented'

	/**
	 * This rail's own accessible name, wherever the rail draws one: the
	 * `aria-label` on a `row` direction's `role="tablist"`, and the name of a
	 * `column` direction's scrollport (`renderColumn()`).
	 *
	 * The `column` landmark itself is named on the host instead — by the
	 * consumer's own `aria-label`/`aria-labelledby`, or by the
	 * `navigationRail.landmark` wording where the consumer named nothing
	 * (`connectedCallback`). A consumer that names the landmark through
	 * `aria-labelledby` has no attribute for the scrollport to read, since an
	 * IDREF does not reach across the shadow boundary — that is the one case
	 * this property carries the column's name on its own.
	 */
	@property({ type: String })
	label = ''

	/**
	 * Whether an item may show its label as a tooltip at all. It never means
	 * one appears: a tooltip repeating a label the user is already reading in
	 * full carries nothing, so each item shows one only while its own label is
	 * hidden by `labelVisibility` or clipped by the box it was given. The item
	 * owns that verdict because the item owns the label element, and only that
	 * element's own overflow can answer the second half
	 * (navigation-rail-item.ts's `labelClipped`). Column direction only.
	 */
	@property({ type: Boolean })
	showTooltips = true

	/**
	 * How many lines each stacked label may take. One (default) is M3's own,
	 * and the column's 80dp measure fits roughly eight characters of
	 * label-medium — enough for `Home` and `Orders`, not for `Ticket
	 * templates` or `Einstellungen`, which arrive on screen chopped. `2` is
	 * M3's maximum for this anatomy and is set on the RAIL rather than per
	 * item, so every row reserves the same height and the column stays a grid.
	 * Column direction, stacked anatomy only.
	 */
	@property({ type: Number, attribute: 'label-lines', reflect: true })
	labelLines: 1 | 2 = 1

	@state()
	private hasHeaderContent = false

	@queryAssignedElements({ flatten: true })
	private allElements!: Element[]

	/**
	 * Where the arrow keys have roved focus to, `-1` while it is still on the
	 * active row. A plain field and not `@state`: it is read only by the
	 * keystroke that follows it, and nothing on screen changes shape when it
	 * moves — the focus ring lives on whichever item actually holds focus.
	 * Shared between both directions' own keyboard handling.
	 */
	#focusedIndex = -1

	private get navigationItems(): SchmancyNavigationRailItem[] {
		return this.allElements.filter(el => el.tagName === 'SCHMANCY-NAVIGATION-RAIL-ITEM') as SchmancyNavigationRailItem[]
	}

	// -------------------------------------------------------------------
	// Nav-rail tree — this rail's `depth`/`items()` are a live projection
	// of the module-scoped registry in `registry.ts`, not a fact resolved
	// once at mount and cached on instance fields. This rail's own job is
	// exactly two calls: `registerRail(this)` on connect, `unregisterRail
	// (this)` on disconnect. Ownership among every currently-registered rail
	// (this one's parent, its children) is derived centrally, via a
	// `computed()` that re-runs the composed-tree climb (`registry.ts`,
	// powered by `discoverAscendingPeer`) on every registration change —
	// which is what lets a rail sit BESIDE its routed content (siblings
	// under a grid) or wrap it, several shadow roots away or not, all with
	// zero consumer markup, and lets a parent that connects a beat after its
	// child resolve correctly with no retry: whoever next reads `depth`/
	// `items()` gets the current derivation, not a stale mount-time snapshot.
	// Both directions of this element share one registry entry — `direction`
	// changes only what this rail draws, never how it is discovered.
	// -------------------------------------------------------------------

	get depth(): number {
		return navRailDepth(this)
	}

	/**
	 * One item source for the rail's own column (or row), an ancestor's
	 * cascade and the mobile puck's drill sheet. `childNode` links the active
	 * item to a currently-registered child rail — set only when this rail's
	 * routed surface has one mounted underneath it right now, which by
	 * default is the adjacent column that rail draws for itself and below
	 * `md` is the level the puck's sheet drills into.
	 */
	items(): NavRailItemDescriptor[] {
		const activeChild = navRailActiveChild(this)
		return this.navigationItems.map(item => ({
			value: item.value || item.label,
			label: item.label,
			icon: item.icon || undefined,
			active: item.active,
			childNode: item.active ? activeChild : null,
		}))
	}

	connectedCallback() {
		super.connectedCallback()

		registerRail(this)

		merge(
			this.activeIndex$.pipe(
				distinctUntilChanged(),
				tap(index => this.updateItemStates(index)),
			),

			// Which rail carries the column and the puck is a lazy read off the registry's derived
			// tree crossed with who is on screen — a rail mounting, unmounting or
			// hiding anywhere moves it with nothing here to notice.
			navRailTreeChanged$.pipe(tap(() => this.requestUpdate())),

			// A rail hidden by its consumer stays registered, so the rails below it
			// learn nothing from the registry — and the puck would stay attached to
			// something nobody can see, taking the surface's whole mobile navigation
			// with it. Two observers, because a hide moves either the box (a
			// `display:none` anywhere above, which this element's own attributes
			// never show) or only the paint (`visibility`, which leaves the box).
			// Intersection rather than resize for the box: measured in Chromium, a
			// ResizeObserver skips a non-replaced inline element, which this host
			// is, and reports the collapse without ever reporting the return.
			// Each observer carries nothing but "look again" — the verdict is
			// re-read here and only a MOVED verdict is published, so a rail merely
			// scrolled out of view never hands the puck to a descendant.
			new Observable<boolean>(subscriber => {
				const publish = () => subscriber.next(navRailIsVisible(this))
				publish()

				const box = new IntersectionObserver(publish)
				box.observe(this)
				const attributes = new MutationObserver(publish)
				attributes.observe(this, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] })
				return () => {
					box.disconnect()
					attributes.disconnect()
				}
			}).pipe(
				distinctUntilChanged(),
				tap(() => navRailVisibilityMoved()),
			),

			// The compact verdict is re-read inside `render()`; this only says
			// when to look again.
			compactColumnChanged$.pipe(tap(() => this.requestUpdate())),

			// A roving tabindex — the item gives itself `0` while active and `-1`
			// otherwise — owes the arrow keys that reach the other rows, or every
			// destination but the current one is unreachable from the keyboard.
			// Only while these items are the rows on screen: absorbed, or drawing
			// a cascade, they are a hidden data source, and roving would swallow
			// the key to move focus among elements nobody renders. Enter and Space
			// are the item's own (navigation-rail-item.ts's `handleKeyDown`).
			// Column direction only — `row` binds its own Left/Right handler
			// directly on the tablist in `renderRow()`, since its focus targets
			// are buttons in THIS shadow root, not the light-DOM items.
			fromEvent<KeyboardEvent>(this, 'keydown').pipe(
				filter(
					() =>
						this.direction === 'column' &&
						this.nestedRails !== 'cascade' &&
						!navRailAbsorbed(this) &&
						this.navigationItems.length > 0,
				),
				map(event => {
					const count = this.navigationItems.length
					const from = this.#focusedIndex >= 0 ? this.#focusedIndex : this.activeIndex
					switch (event.key) {
						case 'ArrowDown':
							return { event, index: (from + 1) % count }
						case 'ArrowUp':
							return { event, index: from <= 0 ? count - 1 : from - 1 }
						case 'Home':
							return { event, index: 0 }
						case 'End':
							return { event, index: count - 1 }
						default:
							return { event, index: -1 }
					}
				}),
				filter(({ index }) => index >= 0),
				tap(({ event, index }) => {
					event.preventDefault()
					this.#focusedIndex = index
					this.navigationItems[index].focus()
				}),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()

		this.setupNavigateListener()

		this.setAttribute('role', 'navigation')
		// Only where the consumer named nothing. Two rails on one screen is this
		// component's ordinary shape — a shell rail and the section rail beside
		// it — and writing this unconditionally gave both the same name, so a
		// screen reader's landmark list offered "Main navigation" twice and
		// neither entry said which one it reached. A default names the common
		// case; it never displaces the name the consumer gave the element.
		if (!this.hasAttribute('aria-label') && !this.hasAttribute('aria-labelledby'))
			this.setAttribute('aria-label', componentText('navigationRail.landmark'))
	}

	/** Symmetric with `connectedCallback`'s register — a rail that unmounts leaves no entry behind. */
	disconnectedCallback() {
		unregisterRail(this)
		super.disconnectedCallback()
	}

	/**
	 * The registry's absorbed verdict, on the host, where CSS can reach it — the
	 * one thing about this element that decides whether it has a box at all
	 * (`static styles`). Re-read every update from `navRailAbsorbed`, the same
	 * call the render branches below make, so the registry stays the single
	 * source and nothing here can hold a verdict the tree has moved past.
	 */
	protected willUpdate(changedProperties: PropertyValues) {
		super.willUpdate(changedProperties)
		this.toggleAttribute('absorbed', navRailAbsorbed(this))

		// All three decide what every row renders, and all are read inside
		// `updateItemStates` rather than by the items themselves — a consumer
		// flipping any of them after mount has nothing else to re-run it.
		// From here and never `updated()`: the pass ends in a registry publish
		// this rail's own tree-changed subscription answers with
		// `requestUpdate()`, which inside `willUpdate` folds into the update
		// already in flight but after `updated()` schedules a trailing cycle —
		// Lit's `change-in-update` warning on every mount, since the three
		// initializers sit in the first update's own `changedProperties`.
		if (
			changedProperties.has('labelVisibility') ||
			changedProperties.has('showTooltips') ||
			changedProperties.has('labelLines') ||
			changedProperties.has('expanded')
		) {
			this.updateItemStates(this.activeIndex)
		}
	}

	/**
	 * Everything this rail decides for its own items, written in one pass:
	 * which one is active, which carry a label, and the string each would show
	 * as a tooltip. Written onto the items rather than passed down, because the
	 * items are the consumer's own light-DOM children in every mode — the rail
	 * projects them for a cascade or a drill, it never owns them. In `row`
	 * direction the items are a hidden data source only (`renderRow()` draws
	 * its own buttons from `items()` instead), so only `active` matters there —
	 * the rest is harmless to set on an element nothing reads.
	 */
	private updateItemStates(index: number) {
		this.navigationItems.forEach((item, i) => {
			const isActive = i === index
			// `aria-selected`/`tabindex` are the item's own answer to its own
			// `active` — set from its `active$` subscription, not written twice
			// from here.
			item.active = isActive
			item.showLabel = this.labelVisibility === 'all' || (this.labelVisibility === 'selected' && isActive)
			item.tooltipText = this.showTooltips && item.label ? item.label : ''
			item.labelLines = this.labelLines
			// M3's expanded column lays icon then label out side by side — the
			// same anatomy a cascade row already carries — instead of the
			// default's stacked one.
			item.expanded = this.expanded

			if (isActive) {
				this.#activeValue = item.value || item.label || ''
			}
		})
		// The next arrow key starts from wherever the app now is, not from the
		// row focus was left on before the selection moved out from under it.
		this.#focusedIndex = -1
		navRailSelectionMoved()
	}

	private updateActiveByValue(value: string) {
		const index = this.navigationItems.findIndex(item => item.getAttribute('value') === value || item.label === value)
		if (index >= 0) {
			this.activeIndex = index
		}
	}

	/** Idempotent — returns the existing item if `config.id` already exists instead of creating a duplicate. */
	public addBoatItem(config: { id: string; title: string; icon?: string }) {
		const existingItem = this.querySelector(`[value="${config.id}"]`) as HTMLElement
		if (existingItem) {
			return existingItem
		}

		const item = document.createElement('schmancy-navigation-rail-item')
		item.setAttribute('value', config.id)
		item.innerHTML = `
			<schmancy-icon slot="icon">${config.icon || 'widgets'}</schmancy-icon>
			${config.title}
		`
		// Add to the rail before any footer content
		const footer = this.querySelector('[slot="footer"]')
		if (footer) {
			this.insertBefore(item, footer)
		} else {
			this.appendChild(item)
		}
		return item
	}

	private handleFabClick(event: Event) {
		event.stopPropagation()
		this.dispatchEvent(
			new CustomEvent('fab-click', {
				bubbles: true,
				composed: true,
			}),
		)
	}

	private handleMenuClick(event: Event) {
		event.stopPropagation()
		this.dispatchEvent(
			new CustomEvent('menu-click', {
				bubbles: true,
				composed: true,
			}),
		)
	}

	/** `row` direction's own tab selection — dispatched from THIS rail, caught by `setupNavigateListener` below like any other `navigate`. */
	private handleRowSelect(item: NavRailItemDescriptor) {
		this.dispatchEvent(new CustomEvent('navigate', { detail: item.value, bubbles: true, composed: true }))
	}

	/**
	 * WAI-ARIA APG "Tabs Pattern" for `row` direction: Left/Right roves focus
	 * AND activates (automatic activation — the model this pattern
	 * recommends for tabs, since the panel swap this rail drives is cheap);
	 * Home/End jump to the first/last tab. Column direction's own Up/Down
	 * handler lives in `connectedCallback` instead — it only moves focus,
	 * since the column's items are Enter/Space-activated
	 * (navigation-rail-item.ts's own `handleKeyDown`).
	 */
	private handleRowKeyDown(event: KeyboardEvent) {
		const items = this.items()
		if (items.length === 0) return

		let newIndex = this.#focusedIndex >= 0 ? this.#focusedIndex : this.activeIndex

		switch (event.key) {
			case 'ArrowRight':
				event.preventDefault()
				newIndex = (newIndex + 1) % items.length
				break
			case 'ArrowLeft':
				event.preventDefault()
				newIndex = newIndex <= 0 ? items.length - 1 : newIndex - 1
				break
			case 'Home':
				event.preventDefault()
				newIndex = 0
				break
			case 'End':
				event.preventDefault()
				newIndex = items.length - 1
				break
			default:
				return
		}

		this.#focusedIndex = newIndex
		this.handleRowSelect(items[newIndex])
		this.updateComplete.then(() => {
			const buttons = this.shadowRoot?.querySelectorAll<HTMLButtonElement>('button[role="tab"]')
			buttons?.[newIndex]?.focus()
		})
	}

	protected render() {
		// Below `md` the puck IS this rail, and exactly one is ever on screen:
		// whoever has no rail above them that anyone can see carries it, in
		// either direction. A consumer hiding a shell rail hands the puck down
		// rather than taking the surface's navigation off with it. `row`
		// carries no puck of its own for the same reason the old standalone
		// horizontal component never did: every real `row` mount sits under a
		// visible `column` ancestor, whose puck already compensates for ITS OWN
		// below-`md` collapse — a problem a row never has, since it renders at
		// every width.
		const carriesPuck = navRailVisibleAncestor(this) === null

		return html`
			<!-- Below md the puck IS this rail. -->
			${when(carriesPuck, () => html`<schmancy-navigation-rail-puck .node=${this}></schmancy-navigation-rail-puck>`)}
			${this.direction === 'row' ? this.renderRow() : this.renderColumn()}
		`
	}

	/**
	 * The tab-strip personality — see the class doc's `direction="row"`
	 * paragraph. Structurally independent of `renderColumn()` below: M3 draws
	 * a rail and a tab bar with different anatomy (icon-column vs plain
	 * button+label), so this draws its own `role="tablist"` of buttons
	 * straight off `items()` rather than reusing
	 * `<schmancy-navigation-rail-item>`'s own render — those items stay
	 * mounted as the hidden data source only, exactly like `renderColumn()`'s
	 * `absorbed`/`cascade` branches already keep them for the SAME reason
	 * (one canonical item source for the puck's drill regardless of who is
	 * actually drawing it).
	 *
	 * No `hidden md:flex` collapse, unlike the column: a row is already a
	 * compact inline strip at any width and has no puck to hand off to below
	 * `md`.
	 */
	private renderRow() {
		const carriesNavigation = !navRailAbsorbed(this)
		const items = this.items()

		const tablistClasses = this.classMap({
			'items-center overflow-x-auto max-w-full': true,

			// segmented: one tonal capsule the tabs sit inside — inline-flex so
			// the capsule hugs its own content rather than stretching full-width.
			'inline-flex gap-1 rounded-full bg-surface-containerHigh p-1': this.variant === 'segmented',

			// scale: a plain row on a hairline baseline — no shared container.
			'flex gap-6 border-b border-outline-variant/30': this.variant === 'scale',

			// elevated: independent cards, no shared track chrome at all.
			'flex gap-2': this.variant === 'elevated',
		})

		return html`
			${when(
				carriesNavigation,
				() =>
					html`<div class="w-full">
						<div
							role="tablist"
							aria-label=${this.label || componentText('navigationRail.tablist')}
							part="tablist"
							class=${tablistClasses}
							@keydown=${this.handleRowKeyDown}
						>
							${repeat(
							items,
							item => item.value,
							(item, index) =>
								choose(this.variant, [
									[
										'segmented',
										() => html`
											<button
												type="button"
												role="tab"
												aria-selected=${item.active}
												tabindex=${item.active ? '0' : '-1'}
												class=${this.classMap({
													'relative shrink-0 cursor-pointer whitespace-nowrap rounded-full px-4 py-2 transition-colors duration-150': true,
													'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default': true,
													'bg-primary-container': item.active,
													'text-surface-onVariant hover:bg-surface-containerHighest hover:text-surface-on':
														!item.active,
												})}
												@click=${() => this.handleRowSelect(item)}
												@focus=${() => (this.#focusedIndex = index)}
											>
												<schmancy-typography
													type="label"
													token="lg"
													class=${this.classMap({ 'text-primary-onContainer font-medium': item.active })}
												>
													${item.label}
												</schmancy-typography>
											</button>
										`,
									],
									[
										'scale',
										() => html`
											<button
												type="button"
												role="tab"
												aria-selected=${item.active}
												tabindex=${item.active ? '0' : '-1'}
												class=${this.classMap({
													'relative shrink-0 cursor-pointer whitespace-nowrap rounded-lg px-2 py-1.5 transition-colors duration-150': true,
													'hover:bg-surface-containerHighest': true,
													'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-surface-on': true,
													'text-surface-on': item.active,
													'text-surface-onVariant': !item.active,
												})}
												@click=${() => this.handleRowSelect(item)}
												@focus=${() => (this.#focusedIndex = index)}
											>
												<schmancy-typography
													type=${item.active ? 'title' : 'label'}
													token=${item.active ? 'sm' : 'lg'}
													class=${this.classMap({ 'font-semibold': item.active })}
												>
													${item.label}
												</schmancy-typography>
												<span
													class=${this.classMap({
														'absolute inset-x-2 -bottom-px h-0.5 rounded-full transition-colors': true,
														'bg-surface-on': item.active,
														'bg-transparent': !item.active,
													})}
												></span>
											</button>
										`,
									],
									[
										'elevated',
										() => html`
											<button
												type="button"
												role="tab"
												aria-selected=${item.active}
												tabindex=${item.active ? '0' : '-1'}
												class=${this.classMap({
													'relative shrink-0 cursor-pointer whitespace-nowrap rounded-xl px-4 py-2.5 transition-all duration-150': true,
													'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-surface-on': true,
													'bg-surface-containerHighest text-surface-on font-medium shadow-md': item.active,
													'bg-surface-containerLow text-surface-onVariant ring-1 ring-outline-variant/30 hover:bg-surface-containerHigh hover:text-surface-on':
														!item.active,
												})}
												@click=${() => this.handleRowSelect(item)}
												@focus=${() => (this.#focusedIndex = index)}
											>
												<schmancy-typography type="label" token="lg">${item.label}</schmancy-typography>
											</button>
										`,
									],
								]),
						)}
						</div>
					</div>`,
			)}
			<!-- The item source, drawn whether or not this row draws its own tabs:
			     absorbed, the column above still projects these items through
			     items(), which reads them off this slot's assignment. -->
			<slot hidden @slotchange=${() => this.handleSlotChange()}></slot>
		`
	}

	/** The vertical column personality — unchanged from before `direction`/`row` existed. */
	private renderColumn() {
		// Whether a column ABOVE this one is already drawing these rows — true
		// only under an ancestor that asked to cascade, so under the default this
		// rail always draws its own column wherever its consumer mounted it.
		const absorbed = navRailAbsorbed(this)

		const cascading = this.nestedRails === 'cascade'

		// THE MEASURE — w-20, M3's 80dp column, the label under the icon and the
		// row's own tooltip carrying whatever a hidden or clipped one costs.
		//
		// w-52 is the CASCADE's own measure, held on every route at any one
		// viewport, and set by the SHALLOWEST level rather than the deepest. A
		// top-level label is a primary destination with nothing above it to name
		// its group, so it reads whole; every level below sits under a parent
		// that already names it and carries a tooltip for whatever its row clips,
		// which is what makes a clipped deep label recoverable and a clipped
		// top-level one not.
		//
		// Measured in Chromium at Geist 14px/500 — the face every consumer here
		// sets through `--schmancy-font-family`, at the weight an ACTIVE row
		// draws, since `font-medium` lands the moment a row is selected and a
		// label sized at the resting weight ellipsises on the one gesture that
		// guarantees the operator is reading that row. The binding row is the
		// widest top-level label this deployment ships, `Connected accounts` at
		// 135.2px: 8px column gutter + a 179px pill (6 + 24 icon + 8 gap + 135
		// label + 6) + a 12px chevron, reserved whether or not that row has a
		// level, + 8px column gutter = 207px. w-52 is the first scale step that
		// clears it — still under M3's own 220dp expanded-rail minimum, which is
		// written for rows that all sit at one level and never spend width on
		// depth.
		//
		// A library measure derived from one consumer's longest CURRENT label is
		// falsified the day that consumer ships a longer one, which is how this
		// arrived: the number was reasoned against `Ticket-Vorlagen`, the
		// deployment added `Connected accounts`, and 21px of label met a ceiling
		// nothing re-read. w-52 is therefore chosen to clear the binding label
		// by 13px rather than to just clear it — headroom is what stops the next
		// destination falsifying this again — and a label that does outrun it
		// still degrades to the tooltip `labelClipped` arms, never to an
		// unreadable row.
		//
		// `expanded` (the M3 property, above) draws the identical w-52 column
		// for the identical reason with no cascade or depth involved at all —
		// reused rather than a second measure invented for a plain top-level
		// widen, so `expanded` and a cascading rail still read as one system.
		//
		// A cascade on a frame that cannot afford 208px falls back to the same
		// 80dp column the default always draws (`compactColumn` above holds the
		// derivation), which is a fact about the VIEWPORT and never about the
		// route — so the content pane's left edge still never moves under an
		// operator who is navigating rather than resizing. `expanded` carries no
		// such fallback — a consumer that sets it wants the wide column
		// unconditionally, the same way it wants any of its other properties.
		//
		// The width is on the host box as well as the inner `railClasses` div: a
		// column must reserve its width in the surrounding layout rather than
		// paint the inner div over whatever sits beside it, and is restated
		// rather than shared between them because a Tailwind class composed at
		// runtime compiles to nothing.
		const wideColumn = this.expanded || (cascading && !(compactColumn?.matches ?? false))

		const hostClasses = this.classMap({
			'hidden md:flex md:flex-col h-full min-h-0': true,
			'w-52': wideColumn,
			'w-20': !wideColumn,
			'box-border relative': true,
			'z-10': true,
		})

		const railClasses = this.classMap({
			flex: true,
			'flex-col h-full min-h-0': true,
			// px-2 — the cascade column's own gutter, on top of the 6px each row's
			// pill already carries, so a row's icon clears the column edge by 14 of
			// M3's 16dp while the column itself pays only 8.
			//
			// px-1 in the narrow column, NOT the 12px that centres M3's 56dp pill:
			// the pill is `inline-flex` and its row centres it on the cross axis,
			// so the column's own padding never positioned it — a 56px pill sits
			// 12px off each edge of an 80px column whatever the padding is. What
			// the padding did decide was the LABEL's box, which is `w-full`, and
			// 56px of an 80px column is 16px the label was paying for a pill that
			// did not need it: `Promotion` measures 63px at label-medium and
			// arrived on screen as `Promoti…`. The header and footer are
			// `items-center` in this column, so their contents do not move either.
			//
			// shadow-lg (M3 elevation 3) rides the wide column alone: at 80dp the
			// rail is chrome its consumer's own surface already grounds, and a
			// second shadow under it reads as a slab laid on that surface.
			'w-52 px-2 shadow-lg': wideColumn,
			'w-20 px-1': !wideColumn,
			'box-border relative': true,
			// No fill: M3 gives the rail its own surface tone, but that container
			// color is chrome the CONSUMER already grounds — `bg-container-lowest`
			// never resolved to a real token (the design system's own is
			// `surface-containerLowest`), so the rail painted nothing, and giving
			// it a working class would only re-add the tone schmancy's own spec
			// deliberately holds this rail without.
			'text-surface-on': true,
		})

		// pb-2 mirrors the footer's own pt-2 — a header affordance (a FAB, a menu,
		// a back button) is not one of the destinations below it, and with no gap
		// it reads as the first row of the list.
		//
		// items-start, not items-center: the rows below start at this column's
		// own leading edge, and an affordance floating in the middle of the
		// column reads as belonging to nothing. The 80dp column is the case
		// where centring is right, because its own rows are centred too.
		const headerClasses = this.classMap({
			'flex flex-col gap-1 pb-2': true,
			'items-center': !wideColumn,
			'items-start': wideColumn,
			hidden: !this.hasHeaderContent,
		})

		// The scrollport is the nav region itself. Its content owns alignment so
		// the header/footer space stays available without a wrapper stealing the
		// flex geometry from the scroller.
		const contentClasses = this.classMap({
			'flex min-h-full flex-col': true,
			'gap-3': !cascading,
			'justify-start': this.alignment === 'top',
			'justify-center': this.alignment === 'center',
			'justify-end': this.alignment === 'bottom',
		})

		const footerClasses = this.classMap({
			'flex flex-col gap-1 mt-auto pt-2': true,
			'items-center': !wideColumn,
			'items-start': wideColumn,
		})

		return html`
			${when(
				!absorbed,
				() => html`
					<div class=${hostClasses}>
						<div class=${railClasses} part="rail">
							<div class=${headerClasses} part="header">
								<slot name="fab" @click=${this.handleFabClick} @slotchange=${this.handleHeaderSlotChange}></slot>
								<slot name="menu" @click=${this.handleMenuClick} @slotchange=${this.handleHeaderSlotChange}></slot>
								<slot name="header" @slotchange=${this.handleHeaderSlotChange}></slot>
							</div>

							<!-- fade, not a scrollbar: a column of destinations that
							     outruns its own height has to say so, and the platform
							     scrollbar cannot — macOS draws an overlay bar that is
							     invisible at rest, which is exactly the moment the
							     operator needs to be told, and reserving a gutter for
							     one would narrow the 80dp column the first time a
							     destination is added. The mask costs no layout and
							     tells the truth in both directions: it appears only
							     while an edge has more past it.

							     A scrollport with more past its edge is also a keyboard
							     stop, and an unnamed stop announces as a bare "region" —
							     indistinguishable from the next rail's. It borrows the
							     landmark's own name rather than minting a second, since
							     the two are one rail; the label property covers the
							     aria-labelledby consumer, whose IDREF cannot reach across
							     this root. -->
							<schmancy-scroll
								hide
								fade
								direction="vertical"
								part="nav"
								label=${this.getAttribute('aria-label') || this.label || componentText('navigationRail.landmark')}
							>
								${when(
									cascading,
									// close is the drill's dismiss signal to the puck's overlay.
									// There is no overlay here and nothing to dismiss, so it stops
									// at this mount rather than surfacing into the consumer's own
									// DOM as an unowned event.
									() => html`
										<div class=${contentClasses}>
											<schmancy-navigation-rail-sheet
												.node=${this}
												.compact=${!wideColumn}
												.showTooltips=${this.showTooltips}
												@close=${(event: Event) => event.stopPropagation()}
											></schmancy-navigation-rail-sheet>
										</div>
									`,
									// The consumer's own items, in the column their consumer
									// mounted this rail to draw. role="list" is what gives each
									// item's own role="listitem" the parent ARIA owes it; the
									// host already carries the navigation landmark.
									() => html`
										<div class=${contentClasses} part="items" role="list">
											<slot @slotchange=${this.handleSlotChange}></slot>
										</div>
									`,
								)}
							</schmancy-scroll>

							<div class=${footerClasses} part="footer">
								<slot name="footer"></slot>
							</div>
						</div>
					</div>
				`,
			)}
			<!-- The item source, wherever the rows themselves are drawn by someone
			     else: an ancestor's cascade, this rail's own, or the puck's drill.
			     All three read the items off this slot's assignment. -->
			${when(absorbed || cascading, () => html`<slot hidden @slotchange=${this.handleSlotChange}></slot>`)}
		`
	}

	private setupNavigateListener() {
		this.addEventListener('navigate', (e: Event) => {
			if (e instanceof CustomEvent) {
				const value = e.detail
				const itemIndex = this.navigationItems.findIndex(item => item.value === value || item.label === value)
				if (itemIndex >= 0) {
					this.activeIndex = itemIndex
					this.#activeValue = value
				}
			}
		})
	}

	private handleHeaderSlotChange() {
		const headerDiv = this.shadowRoot?.querySelector('[part="header"]')
		if (headerDiv) {
			const allSlots = headerDiv.querySelectorAll('slot')
			this.hasHeaderContent = Array.from(allSlots).some(s => s.assignedNodes({ flatten: true }).length > 0)
		}
	}

	private handleSlotChange() {
		// The moment the items become known is the moment `activeValue` can be
		// resolved to one of them: a consumer binds it before its own repeat has
		// slotted anything, so the setter's own resolution finds nothing to
		// match and the rail would carry no selection at all. Resolving here —
		// on a DOM event, outside any update cycle — rather than retrying from
		// `updated()` is what keeps a navigation to one render: the retry set
		// `activeIndex` while the update that triggered it was still finishing,
		// so every navigation scheduled a second render of this rail from inside
		// the first one's own completion.
		this.updateActiveByValue(this.activeValue)
		this.updateItemStates(this.activeIndex)
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-navigation-rail': SchmancyNavigationRail
	}
}
